import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CalendarClock, MessageCircle, MonitorPlay, Sparkles } from 'lucide-react';
import PublicSiteShell from '../components/public/PublicSiteShell';
import DemoVideoCard from '../components/public/DemoVideoCard';
import { demoVideos, whatsappHref } from '../data/publicSite';

import ventasImgUrl from './PuntoDeVenta.png';
import stockImgUrl from './EscanerCompras.png';
import dashboardImgUrl from './image_b95942.png';
import tiendaRopaImgUrl from './tiendaDeRopa.jpeg';
import produccionImgUrl from './ProduccionViandas.png';
import finanzasImgUrl from './FlujoDeFondos.png';

const posterByKey = {
  ventas: ventasImgUrl,
  stock: stockImgUrl,
  dashboard: dashboardImgUrl,
  tiendaRopa: tiendaRopaImgUrl,
  produccion: produccionImgUrl,
  finanzas: finanzasImgUrl,
};

export default function Demo() {
  const location = useLocation();

  // Permite llegar con ancla directa a un video (ej: /demo#video-mostrador).
  useEffect(() => {
    if (!location.hash) return undefined;
    const timeoutId = window.setTimeout(() => {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => window.clearTimeout(timeoutId);
  }, [location.hash]);

  return (
    <PublicSiteShell
      footerCta={{
        eyebrow: 'Sin compromiso',
        title: 'La mejor demo es usarlo con tus propios productos.',
        description:
          'Creás tu cuenta gratis, cargás tres o cuatro productos y hacés una venta de prueba. En diez minutos sabés si Gastify es para tu negocio.',
        primaryLabel: 'Probar Gratis',
        primaryHref: '/registro-onboarding',
        secondaryLabel: 'Hablar por WhatsApp',
        secondaryHref: whatsappHref,
        secondaryExternal: true,
      }}
    >
      <section className="px-4 pb-8 pt-10 md:px-8 md:pb-12 md:pt-14">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-4xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--gst-mint-200)] bg-[var(--surface)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.22em] text-[var(--gst-mint-700)]">
              <MonitorPlay size={13} />
              Demo en video
            </div>
            <h1 className="gst-public-display text-4xl font-black leading-[0.98] tracking-[-0.04em] text-[var(--gst-graphite-900)] sm:text-5xl md:text-7xl">
              Mirá el sistema funcionando.
              <br />
              Sin agendar nada.
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-[var(--gst-graphite-900)] md:text-xl">
              Un video corto por circuito, con operaciones reales: cobrar, reponer, cerrar la caja, vender online y leer los números.
              Mirá solo el que te duele hoy. Ninguno pasa de dos minutos.
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 py-4 md:px-8 md:py-8">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-2 xl:grid-cols-3">
          {demoVideos.map((video) => (
            <DemoVideoCard key={video.id} video={video} poster={posterByKey[video.imageKey]} />
          ))}
        </div>
      </section>

      <section className="px-4 pb-10 pt-6 md:px-8 md:pb-14 md:pt-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <article className="rounded-[2rem] border border-[var(--gst-border)] bg-[var(--gst-graphite-900)] p-8 text-white shadow-[0_24px_50px_rgba(31,36,33,0.18)] md:p-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--surface)]/12 px-3 py-1 text-[11px] font-black uppercase tracking-[0.22em] text-[var(--gst-mint-200)]">
              <CalendarClock size={13} />
              Demo en vivo
            </div>
            <h2 className="gst-public-display mt-5 text-3xl font-black leading-[1.05] sm:text-4xl md:text-5xl">
              ¿Tenés varias sucursales, producción o mucho volumen?
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/88">
              Para operaciones más complejas armamos una demo en vivo sobre tu realidad: tu rubro, tu circuito de ventas
              y tus puntos de control. Veinte minutos, sin vueltas.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/pedir-demo"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--gst-mint-500)] px-6 py-3.5 text-sm font-black uppercase tracking-[0.12em] text-[var(--gst-graphite-900)] transition hover:bg-[var(--gst-mint-400)]"
              >
                Coordinar demo en vivo <ArrowRight size={16} />
              </Link>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-sm font-black uppercase tracking-[0.12em] text-white transition hover:border-[var(--gst-mint-300)] hover:text-[var(--gst-mint-200)]"
              >
                <MessageCircle size={16} /> WhatsApp directo
              </a>
            </div>
          </article>

          <article className="rounded-[2rem] border border-[var(--gst-mint-300)] bg-[linear-gradient(135deg,rgba(209,250,229,0.9),rgba(255,255,255,0.98))] p-8 shadow-sm md:p-10">
            <div className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.22em] text-[var(--gst-mint-700)]">
              <Sparkles size={14} />
              El tercer camino
            </div>
            <h2 className="gst-public-display mt-4 text-3xl font-black leading-tight text-[var(--gst-graphite-900)] sm:text-4xl">
              Tu cuenta de prueba es la demo más honesta.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-[var(--gst-graphite-900)]">
              Los videos muestran cómo se ve. El trial muestra cómo se siente con tus productos, tus precios y tu ritmo.
              Sin tarjeta, sin permanencia.
            </p>
            <Link
              to="/registro-onboarding"
              className="mt-6 inline-flex items-center gap-2 text-sm font-black uppercase tracking-[0.14em] text-[var(--gst-mint-700)] transition hover:gap-3"
            >
              Crear cuenta gratis <ArrowRight size={16} />
            </Link>
          </article>
        </div>
      </section>
    </PublicSiteShell>
  );
}
